/**
 * O armazém do lado do aparelho: um cache que o fluxo mantém em dia.
 *
 * O servidor manda um fluxo só, com tudo. Reabrir um por coleção seria uma
 * conexão por tela; aqui é uma conexão e cada tela pergunta o que quer. É o
 * mesmo desenho do `Armazem` do Flutter (`lib/core/dados/armazem.dart`), para
 * os dois apps enxergarem os mesmos documentos do mesmo jeito.
 */

import type { NossoSempreAPI } from './api/client';
import type { Documento, Evento, QuadroDoFluxo } from './types/dados';
import { ehPronto } from './types/dados';

const ALFABETO = '0123456789abcdefghijklmnopqrstuvwxyz';

/**
 * Identificador novo, gerado no aparelho — igual ao `novoId()` do Flutter.
 *
 * A hora na frente deixa os identificadores em ordem de criação, o que é de
 * graça e evita ordenar por um campo à parte. O acaso atrás resolve o empate
 * de dois aparelhos criando no mesmo milissegundo.
 */
export function novoId(): string {
  const agora = Date.now().toString(36).padStart(9, '0');
  let acaso = '';
  for (let i = 0; i < 8; i++) {
    acaso += ALFABETO[Math.floor(Math.random() * ALFABETO.length)];
  }
  return `${agora}${acaso}`;
}

type Ouvinte = () => void;

export class Armazem {
  private cache = new Map<string, Map<string, Record<string, unknown>>>();
  private ouvintes = new Map<string, Set<Ouvinte>>();
  /** Quem quer saber de `pronto` e `erro`, que não pertencem a coleção nenhuma. */
  private atentos = new Set<Ouvinte>();
  /** Versão por coleção: o `useSyncExternalStore` compara isto, não o objeto. */
  private versao = new Map<string, number>();
  private instantaneos = new Map<string, ReadonlyArray<Documento>>();
  private parar?: () => void;
  private seq = 0;

  /** Fica falso até a carga inicial chegar; sem isso a tela pisca vazia. */
  pronto = false;
  erro: string | null = null;

  constructor(private readonly api: NossoSempreAPI) {}

  ligar(): void {
    if (this.parar) return;
    this.parar = this.api.ouvir(
      (q) => this.receber(q),
      (e) => {
        this.erro = e.message;
        this.avisarEstado();
      },
      this.seq,
    );
  }

  desligar(): void {
    this.parar?.();
    this.parar = undefined;
  }

  private receber(q: QuadroDoFluxo): void {
    if (ehPronto(q)) {
      this.seq = q.seq;
      this.pronto = true;
      this.erro = null;
      this.avisarEstado();
      return;
    }
    const ev = q as Evento;
    this.seq = ev.seq;
    const gaveta = this.cache.get(ev.colecao) ?? new Map();
    if (ev.apagado) gaveta.delete(ev.id);
    else gaveta.set(ev.id, (ev.dados ?? {}) as Record<string, unknown>);
    this.cache.set(ev.colecao, gaveta);
    this.avisar(ev.colecao);
  }

  /**
   * Preenche uma coleção sem esperar o fluxo. Serve para React Native, que não
   * tem `EventSource`: sem fluxo, isto é a única forma de encher a tela.
   */
  async carregar(colecao: string): Promise<void> {
    let p;
    try {
      p = await this.api.ler(colecao);
    } catch (err) {
      // Quem chama muitas vezes não quer tratar cada falha; o estado guarda o
      // recado e a tela mostra o mesmo aviso que mostraria se o fluxo caísse.
      this.erro = (err as Error)?.message ?? 'Não foi possível carregar.';
      this.avisarEstado();
      throw err;
    }
    const gaveta = new Map<string, Record<string, unknown>>();
    for (const d of p.itens) gaveta.set(d.id, d.dados as Record<string, unknown>);
    this.cache.set(colecao, gaveta);
    this.seq = Math.max(this.seq, p.seq);
    this.pronto = true;
    this.avisar(colecao);
    this.avisarEstado();
  }

  /**
   * A coleção como está agora, ordenada por id — que é ordem de criação,
   * porque o id começa com a hora.
   *
   * Devolve sempre a mesma referência enquanto nada mudar: `useSyncExternalStore`
   * entra em laço infinito se cada leitura criar um array novo.
   */
  colecao<T = Record<string, unknown>>(nome: string): ReadonlyArray<Documento<T>> {
    const guardado = this.instantaneos.get(nome);
    if (guardado) return guardado as ReadonlyArray<Documento<T>>;
    const gaveta = this.cache.get(nome);
    const itens: Array<Documento<T>> = gaveta
      ? [...gaveta.keys()].sort().map((id) => ({ id, dados: gaveta.get(id) as T }))
      : [];
    this.instantaneos.set(nome, itens as ReadonlyArray<Documento>);
    return itens;
  }

  documento<T = Record<string, unknown>>(nome: string, id: string): T | null {
    return (this.cache.get(nome)?.get(id) as T) ?? null;
  }

  /** Grava e devolve quando o servidor confirmar. O fluxo traz de volta. */
  async gravar<T extends Record<string, unknown>>(
    colecao: string,
    id: string,
    dados: T,
    mesclar = false,
  ): Promise<void> {
    await this.api.gravar(colecao, id, dados, mesclar);
  }

  /** Grava com id novo, no lugar do `add()`. Devolve o id. */
  async adicionar<T extends Record<string, unknown>>(colecao: string, dados: T): Promise<string> {
    const id = novoId();
    await this.api.gravar(colecao, id, dados);
    return id;
  }

  async apagar(colecao: string, id: string): Promise<void> {
    try {
      await this.api.apagar(colecao, id);
    } catch (err) {
      // Apagar o que já não existe é o resultado que se queria, não um erro.
      if ((err as { status?: number })?.status !== 404) throw err;
    }
  }

  inscrever(colecao: string, ouvinte: Ouvinte): () => void {
    const set = this.ouvintes.get(colecao) ?? new Set();
    set.add(ouvinte);
    this.ouvintes.set(colecao, set);
    return () => set.delete(ouvinte);
  }

  /** Avisa quando `pronto` ou `erro` mudam. */
  inscreverEstado(ouvinte: Ouvinte): () => void {
    this.atentos.add(ouvinte);
    return () => this.atentos.delete(ouvinte);
  }

  versaoDe(colecao: string): number {
    return this.versao.get(colecao) ?? 0;
  }

  private avisar(colecao: string): void {
    this.instantaneos.delete(colecao);
    this.versao.set(colecao, (this.versao.get(colecao) ?? 0) + 1);
    for (const o of this.ouvintes.get(colecao) ?? []) o();
  }

  private avisarEstado(): void {
    for (const o of this.atentos) o();
  }
}
